import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, AlertTriangle, AlertCircle, Info, Wand2,
  CheckCircle2, RefreshCw, X,
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLE = {
  error:   { color: "text-red-400",    bg: "bg-red-500/10",    border: "border-red-500/30",   icon: AlertCircle },
  warning: { color: "text-amber-400",  bg: "bg-amber-500/10",  border: "border-amber-500/30", icon: AlertTriangle },
  info:    { color: "text-sky-400",    bg: "bg-sky-500/10",    border: "border-sky-500/30",   icon: Info },
};

/**
 * Reads the current MTM draft from localStorage and assembles a flat payload
 * the backend audit engine expects.
 */
export function buildLiveOrderPayload() {
  const safe = (k) => {
    try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; }
  };
  const general = safe("mtm_order") || safe("mtm_general") || {};
  const fabric = safe("mtm_fabric") || {};
  const measurements = safe("mtm_measurements") || {};
  const styles = safe("mtm_styles") || {};
  return {
    client_name: general.customerName || general.client_name || "",
    client_id: general.client_id || general.clientId || "",
    garment_type: general.garmentType || general.garment_type || "",
    construction_type: fabric.constructionType || "",
    fabric_selection: fabric.fabricSelection || null,
    lining_selection: fabric.liningSelection || null,
    piping_selection: fabric.pipingSelection || null,
    jacket_button: fabric.jacketButtonSelection || null,
    vest_button: fabric.vestButtonSelection || null,
    shirt_button: fabric.shirtButtonSelection || null,
    overcoat_button: fabric.overcoatButtonSelection || null,
    measurements: measurements?.values || measurements || {},
    styles: styles || {},
  };
}

export function applyAutoFixToLiveOrder(field, value) {
  // Map back to localStorage keys. Only handles fabric/lining/button code fixes.
  try {
    const fabric = JSON.parse(localStorage.getItem("mtm_fabric") || "{}");
    if (field === "fabric_selection.rc_code" && fabric.fabricSelection) {
      fabric.fabricSelection.rc_code = value;
    } else if (field === "lining_selection.rc_code" && fabric.liningSelection) {
      fabric.liningSelection.rc_code = value;
    } else if (field === "piping" && fabric.pipingSelection) {
      fabric.pipingSelection.rc_code = value;
    } else if (field === "jacket_button" && fabric.jacketButtonSelection) {
      fabric.jacketButtonSelection.code = value;
    } else if (field === "vest_button" && fabric.vestButtonSelection) {
      fabric.vestButtonSelection.code = value;
    } else if (field === "shirt_button" && fabric.shirtButtonSelection) {
      fabric.shirtButtonSelection.code = value;
    } else {
      return false;
    }
    localStorage.setItem("mtm_fabric", JSON.stringify(fabric));
    return true;
  } catch {
    return false;
  }
}

const IssueRow = ({ issue, onAutoFix }) => {
  const s = SEVERITY_STYLE[issue.severity] || SEVERITY_STYLE.info;
  const Icon = s.icon;
  return (
    <div className={`border ${s.border} ${s.bg} rounded-md p-2.5 flex items-start gap-2`} data-testid={`audit-issue-${issue.code}`}>
      <Icon size={14} className={`${s.color} shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <code className={`text-[10px] font-mono ${s.color}`}>{issue.code}</code>
          <span className="text-[10px] text-muted-foreground">{issue.field}</span>
        </div>
        <p className="text-xs mt-0.5">{issue.message}</p>
        {issue.suggestion && (
          <p className="text-[11px] text-muted-foreground mt-0.5">{issue.suggestion}</p>
        )}
      </div>
      {issue.auto_fix && onAutoFix && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAutoFix(issue)}
          className="h-7 text-xs shrink-0"
          data-testid={`audit-autofix-${issue.code}`}
        >
          <Wand2 size={11} className="mr-1" /> Fix → {String(issue.auto_fix).slice(0, 18)}
        </Button>
      )}
    </div>
  );
};

/**
 * Auditor component — can be used inline or in a side panel.
 * Props:
 *   payload: object to audit (live draft from buildLiveOrderPayload, or a saved order)
 *   orderId: alternative — audits a saved order by id
 *   onAfterFix: optional callback fired after auto-fix is applied
 *   compact: render in narrow side-panel mode
 */
export const OrderAuditor = ({ payload, orderId, onAfterFix, compact = false }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const runAudit = async () => {
    try {
      setRunning(true);
      const body = orderId ? { order_id: orderId } : { order: payload ?? buildLiveOrderPayload() };
      const res = await axios.post(`${API}/orders/audit`, body);
      setResult(res.data);
      const errs = res.data?.stats?.errors || 0;
      const warns = res.data?.stats?.warnings || 0;
      if (errs === 0 && warns === 0) {
        toast.success("Order looks clean — no issues found");
      } else {
        toast.info(`${errs} error${errs === 1 ? "" : "s"}, ${warns} warning${warns === 1 ? "" : "s"}`);
      }
    } catch (e) {
      toast.error("Audit failed");
    } finally {
      setRunning(false);
    }
  };

  const handleAutoFix = (issue) => {
    if (orderId) {
      toast.info("Auto-fix not supported for saved orders yet — edit the order to apply.");
      return;
    }
    const ok = applyAutoFixToLiveOrder(issue.field, issue.auto_fix);
    if (ok) {
      toast.success(`Applied: ${issue.field} → ${issue.auto_fix}`);
      onAfterFix?.();
      // Re-run audit so the fixed issue disappears
      runAudit();
    } else {
      toast.error("Couldn't apply auto-fix — try editing manually.");
    }
  };

  const grouped = result?.issues ? result.issues.reduce((acc, i) => {
    (acc[i.severity] = acc[i.severity] || []).push(i);
    return acc;
  }, {}) : {};

  return (
    <div className={`bg-card border border-white/10 rounded-lg ${compact ? "" : "p-4"}`}>
      <div className={`flex items-center justify-between ${compact ? "p-3 border-b border-white/5" : "mb-3"}`}>
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-emerald-400" />
          <h3 className="text-sm font-medium">Order QA</h3>
        </div>
        <Button
          size="sm"
          variant={result ? "outline" : "default"}
          onClick={runAudit}
          disabled={running}
          data-testid="order-audit-run-btn"
        >
          {running ? (
            <><RefreshCw size={12} className="mr-1 animate-spin" /> Auditing…</>
          ) : result ? (
            <><RefreshCw size={12} className="mr-1" /> Re-audit</>
          ) : (
            <><ShieldCheck size={12} className="mr-1" /> Audit Order</>
          )}
        </Button>
      </div>

      <div className={compact ? "p-3 max-h-[420px] overflow-y-auto" : ""}>
        <AnimatePresence>
          {!result && !running && (
            <p className="text-xs text-muted-foreground" data-testid="audit-empty-state">
              Click <strong>Audit Order</strong> to validate fabric codes, button codes,
              measurements, style completeness, and detect duplicates before submitting to RCMTM.
            </p>
          )}

          {result && result.stats.errors === 0 && result.stats.warnings === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2 text-emerald-400 text-sm py-2"
              data-testid="audit-clean-banner"
            >
              <CheckCircle2 size={16} /> Order is clean — safe to submit.
            </motion.div>
          )}

          {result && (result.stats.errors > 0 || result.stats.warnings > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3"
              data-testid="audit-issues-list"
            >
              <div className="flex items-center gap-3 text-xs">
                {result.stats.errors > 0 && (
                  <span className="text-red-400">{result.stats.errors} error{result.stats.errors === 1 ? "" : "s"}</span>
                )}
                {result.stats.warnings > 0 && (
                  <span className="text-amber-400">{result.stats.warnings} warning{result.stats.warnings === 1 ? "" : "s"}</span>
                )}
                {result.stats.info > 0 && (
                  <span className="text-sky-400">{result.stats.info} info</span>
                )}
              </div>

              {["error", "warning", "info"].map((sev) =>
                grouped[sev]?.length ? (
                  <div key={sev} className="space-y-1.5">
                    {grouped[sev].map((issue, idx) => (
                      <IssueRow
                        key={`${issue.code}-${idx}`}
                        issue={issue}
                        onAutoFix={issue.auto_fix ? handleAutoFix : null}
                      />
                    ))}
                  </div>
                ) : null
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OrderAuditor;
